/**
 * Gestionnaire pour les templates IA et les actions de tâches
 * Utilise handlebars-manager.js pour l'infrastructure Handlebars
 * 
 * Architecture:
 * - handlebars-manager.js : Chargement, compilation et exécution des templates
 * - handlebarIA-services.js : Logique métier (IA, traitement post-template)
 */

import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';
import { 
  loadSingleTemplate, 
  renderTemplate, 
  getTemplateMetadata, 
  clearTemplateCache,
  initializeHandlebarsHelpers 
} from './handlebars-manager.js';
import { convertBBCodeToHtml } from './bbcodeHelpers.js';
import { getAiWorkingLanguage } from '../utils/routing-config-manager.js';
import { initializeGlossaryHelper } from './glossaryHelpers.js';
import { callIA } from './ia-services.js';
import waproxyClient from '../utils/waproxy-calls.js';

/**
 * Décode toutes les entités HTML (nommées, décimales et hexadécimales)
 * @param {string} text - Texte contenant des entités HTML
 * @returns {string} - Texte avec entités décodées
 */
function decodeHtmlEntities(text) {
  // Créer un élément temporaire pour décoder les entités HTML
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  let decoded = textarea.value;
  
  // Gérer les entités numériques décimales (&#xxx;)
  decoded = decoded.replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec));
  
  // Gérer les entités numériques hexadécimales (&#xXX;)
  decoded = decoded.replace(/&#x([0-9a-fA-F]+);/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)));
  
  // Entités nommées courantes (backup si textarea ne les décode pas)
  const entities = {
    '&nbsp;': ' ',
    '&lt;': '<',
    '&gt;': '>',
    '&amp;': '&',
    '&quot;': '"',
    '&apos;': "'",
    '&#39;': "'",
    '&#x27;': "'",
    '&eacute;': 'é',
    '&egrave;': 'è',
    '&ecirc;': 'ê',
    '&euml;': 'ë',
    '&agrave;': 'à',
    '&acirc;': 'â',
    '&auml;': 'ä',
    '&ccedil;': 'ç',
    '&ocirc;': 'ô',
    '&iuml;': 'ï',
    '&ucirc;': 'û',
    '&ugrave;': 'ù',
    '&rsquo;': "'",
    '&lsquo;': "'",
    '&rdquo;': '"',
    '&ldquo;': '"',
    '&mdash;': '—',
    '&ndash;': '–',
    '&hellip;': '…',
  };
  
  Object.entries(entities).forEach(([entity, char]) => {
    decoded = decoded.replaceAll(entity, char);
  });
  
  return decoded;
}

/**
 * Convertit du HTML en Markdown
 * Gère les balises de base pour une conversion simple
 * @param {string} html - Code HTML à convertir
 * @returns {string} - Code Markdown converti
 */
function convertHtmlToMarkdown(html) {
  let markdown = html;
  
  // Titres
  markdown = markdown.replace(/<h1[^>]*>(.*?)<\/h1>/gi, '## $1\n\n');
  markdown = markdown.replace(/<h2[^>]*>(.*?)<\/h2>/gi, '### $1\n\n');
  markdown = markdown.replace(/<h3[^>]*>(.*?)<\/h3>/gi, '#### $1\n\n');
  markdown = markdown.replace(/<h4[^>]*>(.*?)<\/h4>/gi, '##### $1\n\n');
  markdown = markdown.replace(/<h5[^>]*>(.*?)<\/h5>/gi, '###### $1\n\n');
  markdown = markdown.replace(/<h6[^>]*>(.*?)<\/h6>/gi, '###### $1\n\n');
  
  // Gras et italique
  markdown = markdown.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**');
  markdown = markdown.replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**');
  markdown = markdown.replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*');
  markdown = markdown.replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*');
  
  // Liens
  markdown = markdown.replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)');
  
  // Images
  markdown = markdown.replace(/<img[^>]*src="([^"]*)"[^>]*alt="([^"]*)"[^>]*>/gi, '![$2]($1)');
  markdown = markdown.replace(/<img[^>]*src="([^"]*)"[^>]*>/gi, '![]($1)');
  
  // Listes
  markdown = markdown.replace(/<ul[^>]*>/gi, '');
  markdown = markdown.replace(/<\/ul>/gi, '\n');
  markdown = markdown.replace(/<ol[^>]*>/gi, '');
  markdown = markdown.replace(/<\/ol>/gi, '\n');
  markdown = markdown.replace(/<li[^>]*>(.*?)<\/li>/gi, '- $1\n');
  
  // Blocs de code
  markdown = markdown.replace(/<pre[^>]*><code[^>]*>(.*?)<\/code><\/pre>/gis, '```\n$1\n```\n');
  markdown = markdown.replace(/<code[^>]*>(.*?)<\/code>/gi, '`$1`');
  
  // Citations
  markdown = markdown.replace(/<blockquote[^>]*>(.*?)<\/blockquote>/gis, (match, content) => {
    return content.split('\n').map(line => '> ' + line).join('\n') + '\n';
  });
  
  // Paragraphes et sauts de ligne
  markdown = markdown.replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n');
  markdown = markdown.replace(/<br[^>]*>/gi, '\n');
  markdown = markdown.replace(/<hr[^>]*>/gi, '\n---\n\n');
  
  // Nettoyer les balises restantes
  markdown = markdown.replace(/<\/?[^>]+>/g, '');
  
  // Décoder toutes les entités HTML
  markdown = decodeHtmlEntities(markdown);
  
  // Nettoyer les espaces multiples et lignes vides excessives
  markdown = markdown.replace(/ +/g, ' ');
  markdown = markdown.replace(/\n{3,}/g, '\n\n');
  
  // S'assurer que chaque titre est précédé d'exactement \n\n (sauf au début)
  markdown = markdown.replace(/\n*(#{1,6}\s)/g, '\n\n$1');
  markdown = markdown.trim();
  
  return markdown;
}

/**
 * Convertit du HTML en texte brut
 * Retire toutes les balises HTML et nettoie le contenu
 * @param {string} html - Code HTML à convertir
 * @returns {string} - Texte brut converti
 */
function convertHtmlToText(html) {
  let text = html;
  
  // Remplacer les balises de bloc par des sauts de ligne
  text = text.replace(/<\/?(p|div|h[1-6]|br|hr|blockquote|pre|ul|ol|li)[^>]*>/gi, '\n');
  
  // Retirer toutes les autres balises HTML
  text = text.replace(/<\/?[^>]+>/g, '');
  
  // Décoder toutes les entités HTML
  text = decodeHtmlEntities(text);
  
  // Nettoyer les espaces multiples
  text = text.replace(/ +/g, ' ');
  
  // Nettoyer les sauts de ligne excessifs
  text = text.replace(/\n{3,}/g, '\n\n');
  
  // Nettoyer les espaces en début/fin de lignes
  text = text.split('\n').map(line => line.trim()).join('\n');
  
  // Nettoyer les espaces en début/fin de texte
  text = text.trim();
  
  return text;
}

/**
 * Récupère la langue de travail IA depuis localStorage
 * @returns {Promise<string>} Code langue (FR, EN, etc.)
 */
async function getWorkingLanguage() {
  const lang = await getAiWorkingLanguage();
  return lang || localStorage.getItem('ui_language') || 'FR';
}

/**
 * Initialise le glossaire
 * À appeler une fois au démarrage si besoin
 * @param {string} languageTo - Langue de travail (optionnel)
 */
export const initializeGlossary = async (languageTo = '') => {
  try {
    debug.log(config, `handlebarIA-services: Initialisation du glossaire pour la langue: ${languageTo || 'défaut'}`);
    await initializeGlossaryHelper('', languageTo);
  } catch (error) {
    debug.warn(config, 'handlebarIA-services: Erreur glossaire:', error);
  }
};

/**
 * Calcule la clé de template à partir d'un objet action
 * @param {Object} action - L'objet action contenant les propriétés nécessaires
 * @param {string} [agent] - Agent optionnel à ajouter à la clé (sous forme de @agent)
 * @returns {string} - La clé de template formatée
 */
export const calculateTemplateKey = (action, agent = null) => {
  // Si action est null et agent est une chaîne non vide, retourner agent
  if (action === null && typeof agent === 'string' && agent.trim() !== '') {
    debug.log(config, `[IA Services] Template key from agent: ${agent}`);
    return agent;
  }
  
  // Vérifier que action est bien un objet
  if (typeof action !== 'object' || action === null) {
    debug.error(config, '[IA Services] Invalid action type (must be object)');
    return null;
  }
   
  let templateKey = `${action.task_name}#${action.function_tag}-${action.works_on}${agent}`;

  debug.log(config, `[IA Services] Calculated template key: ${templateKey}`);
  return templateKey;
};

/**
 * Exécute un template Handlebars avec les données fournies et un objet action
 * Charge le template à la demande depuis l'API (avec cache automatique)
 * @param {Object} action - Objet action contenant les informations du template
 * @param {Object} data - Données à passer au template
 * @param {string} [agent] - Agent optionnel pour l'action
 * @param {string} [workingLanguage] - Langue de travail (défaut: depuis localStorage)
 * @param {string} [outputFormat='html'] - Format de sortie souhaité ('html', 'markdown' ou 'text')
 * @returns {Promise<Object>} - {text: string, metadata: object}
 */
export const runTemplate = async (action, data, agent = null, workingLanguage = null, outputFormat = 'html') => {
  let metadata = {};

  // Récupérer la langue de travail
  const lang = workingLanguage || await getWorkingLanguage();
  
  // Calculer la clé du template
  const templateKey = calculateTemplateKey(action, agent);
  
  // Charger le template à la demande
  const template = await loadSingleTemplate(lang, templateKey);
  
  if (!template) {
    debug.warn(config, `handlebarIA-services: Template non trouvé: ${templateKey}`);
    return { text: null, metadata: null };
  }
  
  try {
    const localData = { ...data };
    debug.log(config, `[IA Services] Executing template: ${template.key}`, localData);

    // Initialiser le glossaire avec la langue de travail
    if (lang) {
      await initializeGlossary(lang);
    }

    // STEP 1: Exécuter le template avec renderTemplate()
    let result = await renderTemplate(template, localData);

    // STEP 2: Traitement des appels spéciaux
    let stepSlices = result.split(/(@!|!@)/);
    let childrenId = [];
    result = '';

    for (let i = 0; i < stepSlices.length; i++) {
      if (stepSlices[i] === '@!') {
        i++;
        const theCommandSlices = stepSlices[i].split('!');
        debug.log(config, `[IA Services] Processing ${theCommandSlices[0]}`);

        switch (theCommandSlices[0]) {
          case 'DescribePortrait':
          case 'DescribeImage': {
            let theURL;
            if (/^\d+$/.test(theCommandSlices[1])) {
              const response = await waproxyClient.waGet('/image', theCommandSlices[1], { granularity: 1 });
              theURL = response.url;
            } else {
              theURL = theCommandSlices[1];
            }

            const theCall = [];
            
            // Charger et exécuter le template system pour cet helper
            const systemTemplateKey = calculateTemplateKey(null, `${theCommandSlices[0]}@system`);
            const systemTemplate = await loadSingleTemplate(lang, systemTemplateKey);
            if (systemTemplate) {
              const SystemResult = await renderTemplate(systemTemplate, { url: theURL });
              if (SystemResult) {
                theCall.push({
                  IA: systemTemplate.use,
                  role: 'system',
                  temperature: systemTemplate.temperature,
                  text: SystemResult,
                  url: null,
                });
              }
            }
            
            // Charger et exécuter le template assistant pour cet helper
            const assistantTemplateKey = calculateTemplateKey(null, `${theCommandSlices[0]}@assistant`);
            const assistantTemplate = await loadSingleTemplate(lang, assistantTemplateKey);
            if (assistantTemplate) {
              const AssistantResult = await renderTemplate(assistantTemplate, { url: theURL });
              if (AssistantResult) {
                theCall.push({
                  IA: assistantTemplate.use,
                  role: 'assistant',
                  temperature: assistantTemplate.temperature,
                  text: AssistantResult,
                  url: null,
                });
              }
            }
            
            // Charger et exécuter le template user pour cet helper
            const userTemplateKey = calculateTemplateKey(null, `${theCommandSlices[0]}@user`);
            const userTemplate = await loadSingleTemplate(lang, userTemplateKey);
            if (userTemplate) {
              const UserResult = await renderTemplate(userTemplate, { url: theURL });
              if (UserResult) {
                theCall.push({
                  IA: userTemplate.use,
                  role: 'user',
                  temperature: userTemplate.temperature,
                  text: UserResult,
                  url: theURL,
                });
              }
            }

            debug.log(config, `[IA Services] IA call ${theCommandSlices[0]} with URL: ${theURL}`);
            result += (await callIA(theCall, lang)).data;
            break;
          }
          case 'StoreId': {
            childrenId.push(theCommandSlices[1]);
            break;
          }
          default: {
            debug.error(config, `[IA Services] Unknown helper: ${theCommandSlices[0]}`);
          }
        }
        i++;
      } else {
        result += stepSlices[i];
      }
    }
    
    // STEP 3: Traitement selon le format de données (conversion vers HTML comme format pivot)
    switch (template.dataformat) {
      case 'bbcode':
        result = result.replace(/@\[(.+?)\]\(.+?\)/gi, '$1');
        result = await convertBBCodeToHtml(result);
        break;
      case 'html':
        break;
      case 'text':
        break;
    }

    // STEP 4: Traitement selon le format de sortie souhaité
    switch (outputFormat) {
      case 'markdown':
        result = convertHtmlToMarkdown(result);
        result = result.replace(/[^\S\n]+\n/g, '\n').replace(/\n{3,}/g, '\n\n');
        break;
      case 'text':
        result = convertHtmlToText(result);
        result = result.replace(/[^\S\n]+\n/g, '\n').replace(/\n{3,}/g, '\n\n');
        break;
      case 'html':
      default:
        result = result.replace(/[^\S\n]+\n/g, '\n').replace(/\n{3,}/g, '\n\n');
        // Rien à faire, on garde le HTML
        break;
    }

    metadata = { ...metadata, childrenId };
    debug.log(config, `[IA Services] Result ${template.key}`, {text: result, metadata});
    return { text: result, metadata };

  } catch (error) {
    debug.error(config, `[IA Services] Error executing ${template.key}:`, error);
    return { text: null, metadata: null };
  }
};

/**
 * Récupère les métadonnées d'un template à partir d'un objet action
 * Utilisé par actionCardHandler pour accéder aux propriétés du template (use, temperature, etc.)
 * @param {Object} action - Objet action ou null
 * @param {string} [agent] - Agent optionnel
 * @param {string} [workingLanguage] - Langue de travail (défaut: depuis localStorage)
 * @returns {Promise<Object|null>} - Métadonnées du template (use, temperature, dataformat, etc.)
 */
export const getTemplateData = async (action, agent = null, workingLanguage = null) => {
  const lang = workingLanguage || await getWorkingLanguage();
  const templateKey = calculateTemplateKey(action, agent);
  
  const template = await loadSingleTemplate(lang, templateKey);
  
  return getTemplateMetadata(template);
};
